/* eslint-disable import/no-default-export */
import type { CustomDetector, CookieOptions } from 'i18next-browser-languagedetector';
import { getParsedLanguage } from './utils/supportedLocales';

// copy from i18next-browser-languagedetector localStorage detector
let hasLocalStorageSupport: boolean | null = null;

const localStorageAvailable = () => {
  if (hasLocalStorageSupport !== null) return hasLocalStorageSupport;

  try {
    hasLocalStorageSupport = typeof window !== 'undefined' && window.localStorage !== null;
    const testKey = 'i18next.translate.boo';

    window.localStorage.setItem(testKey, 'foo');
    window.localStorage.removeItem(testKey);
  } catch (e) {
    hasLocalStorageSupport = false;
  }

  return hasLocalStorageSupport;
};

// copy from i18next-browser-languagedetector cookie detector
/* eslint-disable no-control-regex */
const fieldContentRegExp = /^[\u0009\u0020-\u007e\u0080-\u00ff]+$/;

const serializeCookie = (name: string, val: string, options: CookieOptions) => {
  const opt = options ?? {};
  opt.path = opt.path ?? '/';

  const value = encodeURIComponent(val);
  let str = `${name}=${value}`;

  if (opt.maxAge !== undefined && opt.maxAge > 0) {
    const maxAge = opt.maxAge - 0;

    if (Number.isNaN(maxAge)) {
      throw new Error('maxAge should be a Number');
    }

    str += `; Max-Age=${Math.floor(maxAge)}`;
  }

  if (opt.domain !== undefined) {
    if (!fieldContentRegExp.test(opt.domain)) {
      throw new TypeError('option domain is invalid');
    }

    str += `; Domain=${opt.domain}`;
  }

  if (opt.path) {
    if (!fieldContentRegExp.test(opt.path)) {
      throw new TypeError('option path is invalid');
    }

    str += `; Path=${opt.path}`;
  }

  if (opt.expires) {
    if (typeof opt.expires.toUTCString !== 'function') {
      throw new TypeError('option expires is invalid');
    }

    str += `; Expires=${opt.expires.toUTCString()}`;
  }

  if (opt.httpOnly !== undefined && opt.httpOnly) {
    str += '; HttpOnly';
  }

  if (opt.secure !== undefined && opt.secure) {
    str += '; Secure';
  }

  if (opt.sameSite !== undefined) {
    const sameSite = typeof opt.sameSite === 'string' ? opt.sameSite.toLowerCase() : opt.sameSite;
    switch (sameSite) {
      case true:
        str += '; SameSite=Strict';
        break;
      case 'lax':
        str += '; SameSite=Lax';
        break;
      case 'strict':
        str += '; SameSite=Strict';
        break;
      case 'none':
        str += '; SameSite=None';
        break;
      default:
        throw new TypeError('option sameSite is invalid');
    }
  }
  return str;
};

// copy from i18next-browser-languagedetector cookie detector
/* eslint-disable func-params-args/func-params */
const cookie = {
  create: (
    name: string,
    value: string,
    minutes: number | undefined,
    domain: string | undefined,
    cookieOptions: CookieOptions = { path: '/', sameSite: 'strict' },
  ) => {
    const options = cookieOptions;

    if (minutes !== undefined) {
      options.expires = new Date();
      options.expires.setTime(options.expires.getTime() + minutes * 60 * 1000);
    }

    if (domain !== undefined) {
      options.domain = domain;
    }

    document.cookie = serializeCookie(name, encodeURIComponent(value), cookieOptions);
  },
  read(name: string) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];

      while (c.charAt(0) === ' ') c = c.substring(1, c.length);

      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }

    return null;
  },
};

export default {
  name: 'hotjar',
  lookup: (options) => {
    let foundLang: string | undefined;

    // priority in taking lang has local storage
    if (options.lookupLocalStorage !== undefined && localStorageAvailable()) {
      const lng = window.localStorage.getItem(options.lookupLocalStorage);

      if (lng !== null) {
        foundLang = lng;
      }
    }

    // if no lang in local storage take it from NEXT_LOCALE cookie
    if (
      options.lookupCookie !== undefined &&
      typeof document !== 'undefined' &&
      foundLang === undefined
    ) {
      const cookieContent = cookie.read(options.lookupCookie);

      if (cookieContent !== null) {
        foundLang = cookieContent;
      }
    }

    // if no lang in local storage and cookie take it from navigator
    if (typeof navigator !== 'undefined' && foundLang === undefined) {
      if (navigator.languages.length > 0) {
        const [firstElem] = navigator.languages;
        foundLang = firstElem;
      }

      if (navigator.language) {
        foundLang = navigator.language;
      }
    }

    const parsedLang = getParsedLanguage(foundLang);

    if (parsedLang !== undefined) {
      return parsedLang;
    }

    return foundLang;
  },
  cacheUserLanguage: () => undefined,
} as CustomDetector;
